import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';

import InputLogoField from '@/components/common/InputLogoField';
import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import firebase from '@/shared/services/firebase';
import logger from '@/shared/utils/logger';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';
import keyIcon from '@/assets/img/icons/svg/keyIcon.svg';
import eyePasswordHide from '@/assets/img/icons/svg/eyePasswordHide.svg';
import eyePasswordShow from '@/assets/img/icons/svg/eyePasswordShow.svg';

import './LogIn.css';

const LogIn = () => {
    // State variables for form validation and submission
    const [validated, setValidated] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errors, setErrors] = useState({});

    // State to lock features during loading
    const [loading, setLoading] = useState(false);

    // Form data state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // State to track which fields have been touched for validation
    const [touched, setTouched] = useState({
        email: false,
        password: false,
    });

    // Hook to navigate programmatically
    const navigate = useNavigate();

    // Hook to get search parameters
    const [searchParams] = useSearchParams();

    const { login, reloadCurrentUser } = useAuth();

    /** * Validates the form values.
     * This function checks if
     * the email is in a valid format, and if the password meets the strength criteria.
     * It returns an object containing error messages for each field that fails validation.
     * @param {Object} values - The form values to validate.
     * @returns {Object} - An object containing error messages for invalid fields.
     */
    const validate = (values) => {
        const errors = {};
        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!emailRegex.test(values.email)) {
            errors.email = 'Please provide a valid email.';
        }
        if (values.password.length < 3) {
            errors.password = 'Please enter your password.';
        }
        return errors;
    };

    /**
     * Handles the user login process.
     *
     * This function validates the form data, checks if email is in proper format and ,
     * at least two characters are entered for password. If validation passes, it logs in
     * the user using Firebase Authentication.
     *
     * On success, it resets the form, displays a success message, and navigates to the intended page or profile page after a delay.
     * On failure, it sets the error state with the error message(s).
     *
     * @async
     * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
     * @returns {Promise<void>}
     */
    const handleLogIn = async (event) => {
        event.preventDefault();

        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({
            email: true,
            password: true,
        });

        // returns early if errors exist
        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);

        try {
            // log in with Firebase through AuthProvider
            await login(formData.email, formData.password);

            // Reload to get latest emailVerified status, then read from
            // auth.currentUser directly to avoid stale closure reference.
            await reloadCurrentUser();
            const currentUser = firebase.auth.currentUser;

            if (!currentUser?.emailVerified) {
                setErrors({
                    email: 'Please verify your email before logging in. Check your inbox for the verification link.',
                    password: ' ',
                });
                return;
            }

            // Use redirect parameter system from develop-bili-bot
            const redirectTo = searchParams.get('redirect');

            if (redirectTo) {
                // If there's a redirect URL, go there immediately without success message
                navigate(redirectTo, { replace: true });
            } else {
                // No redirect URL - show success message and go to dashboard (bili-bot default)
                setSubmitSuccess(true);
                setFormData({ email: '', password: '' });
                setErrors({});
                setValidated(false);
                setTouched({ email: false, password: false });

                setTimeout(() => {
                    setSubmitSuccess(false);
                    navigate('/dashboard'); // Use dashboard as default like bili-bot
                }, 2000);
            }
        } catch (error) {
            setErrors({
                email: 'Login failed. Please try again.',
                password: ' ',
            });

            logger.error('Login failed. Please try again. ', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Log In"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Log In', path: '/login' },
                ]}
            />
            <div
                id="login"
                className="page-container bg-primary d-flex justify-content-center align-items-center py-4"
            >
                <Container className="m-auto py-5">
                    <Form
                        noValidate
                        onSubmit={handleLogIn}
                        className="d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-3"
                    >
                        {submitSuccess && (
                            // placeholder for actual response
                            <div
                                className="alert alert-success text-center w-100"
                                role="alert"
                            >
                                Login successful! Redirecting...
                            </div>
                        )}
                        <Row className="mb-1">
                            <h1 className="text-primary text-center">
                                Log In to your Account
                            </h1>
                        </Row>

                        <Row className="flex-xl-nowrap">
                            <Form.Group as={Col} xl="6" className="mb-3">
                                <InputLogoField
                                    icon={emailIcon}
                                    controlId="floatingEmail"
                                    label="Email"
                                    hasValidation
                                >
                                    <Form.Control
                                        type="email"
                                        placeholder="name@example.com"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            const { name, value } = e.target;

                                            const updatedFormData = {
                                                ...formData,
                                                [name]: value,
                                            };

                                            setFormData(updatedFormData);
                                            setErrors(
                                                validate(updatedFormData)
                                            );
                                        }}
                                        onBlur={(e) => {
                                            const { name } = e.target;

                                            setTouched({
                                                ...touched,
                                                [name]: true,
                                            });

                                            setErrors(validate(formData));
                                        }}
                                        isInvalid={
                                            touched.email && !!errors.email
                                        }
                                        isValid={touched.email && !errors.email}
                                        disabled={loading}
                                    />
                                </InputLogoField>
                                {validated && errors.email && (
                                    <div className="invalid-feedback d-block fw-semibold ps-3">
                                        {errors.email}
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group as={Col} xl="6" className="mb-3">
                                <InputLogoField
                                    icon={keyIcon}
                                    controlId="floatingPassword"
                                    label="Password"
                                    hasValidation
                                    className="position-relative"
                                    append={
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            className="position-absolute top-50 end-0 translate-middle-y me-2 bg-transparent border-0 p-0"
                                            aria-label={
                                                showPassword
                                                    ? 'Hide password'
                                                    : 'Show password'
                                            }
                                        >
                                            <img
                                                src={
                                                    showPassword
                                                        ? eyePasswordShow
                                                        : eyePasswordHide
                                                }
                                                alt=""
                                                aria-hidden="true" // hidden bc aria labeling is handled above w/in "button"
                                            />
                                        </button>
                                    }
                                >
                                    <Form.Control
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        placeholder="Enter your password"
                                        value={formData.password}
                                        onChange={(e) => {
                                            const { name, value } = e.target;

                                            const updatedFormData = {
                                                ...formData,
                                                [name]: value,
                                            };

                                            setFormData(updatedFormData);
                                            setErrors(
                                                validate(updatedFormData)
                                            );
                                        }}
                                        onBlur={(e) => {
                                            const { name } = e.target;

                                            setTouched({
                                                ...touched,
                                                [name]: true,
                                            });
                                            setErrors(validate(formData));
                                        }}
                                        isInvalid={
                                            touched.password &&
                                            !!errors.password
                                        }
                                        isValid={
                                            touched.password && !errors.password
                                        }
                                        disabled={loading}
                                    />
                                </InputLogoField>
                                {validated && errors.password && (
                                    <div className="invalid-feedback d-block fw-semibold ps-3">
                                        {errors.password}
                                    </div>
                                )}
                            </Form.Group>
                        </Row>

                        <Row className="mt-4 mx-auto">
                            <Button
                                className="login-btn btn-lg bg-primary text-white text-center fw-bold m-auto py-3"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? 'Logging in...' : 'Log In'}
                            </Button>
                            <Col sm={12} className="mt-3 text-center">
                                Forgot your password?
                                <Link
                                    to="/forgot-password"
                                    className="text-info fw-bold ps-1 text-decoration-none"
                                >
                                    Reset here
                                </Link>
                            </Col>
                            <Col sm={12} className="mt-3 text-center">
                                Don&apos;t have an account?
                                <Link
                                    to="/signup"
                                    className="text-info fw-bold ps-1 text-decoration-none"
                                >
                                    Sign Up here
                                </Link>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </div>
        </>
    );
};

export default LogIn;
